import { useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../api/client'
import type { ReferenceCounts, ReferenceProposal, ReferenceRow, ReferenceSourceKey, ReferenceStatus } from '../api/types'
import { useAuth } from '../auth/AuthContext'
import { fogliConDispositivi, leggiFoglio, tabellaTsv } from '../foglio'
import { useAsync } from '../hooks/useAsync'
import { localeCorrente, t } from '../i18n/lingua'
import { Card, ErrorBanner, Spinner, StatCard } from './common'
import { DataTable } from './DataTable'

/**
 * Il confronto con un inventario di riferimento (Armis, Medigate, il
 * gestionale dell'Ingegneria Clinica): quanti dei dispositivi di quell'elenco
 * CENSO vede, e quanti ne riconosce. È il numero con cui si misura il
 * riconoscimento degli elettromedicali, e il punto da cui il catalogo CENSO
 * impara le firme (prefisso MAC, impronta DHCP) per gli altri ospedali.
 *
 * Il file lo legge il browser (`foglio.ts`) e arriva al Control Plane come
 * testo: la mappatura delle colonne la fa il server.
 */

const FONTI: { key: ReferenceSourceKey; label: string }[] = [
  { key: 'armis', label: 'Armis' },
  { key: 'medigate', label: 'Medigate' },
  { key: 'cmms', label: 'Ingegneria Clinica' },
  { key: 'other', label: 'Altro' },
]

function etichettaFonte(k: string): string {
  const f = FONTI.find((x) => x.key === k)
  return f ? (f.key === 'cmms' ? t('Ingegneria Clinica') : f.key === 'other' ? t('Altro') : f.label) : k
}

export function etichettaStato(s: ReferenceStatus): string {
  switch (s) {
    case 'model': return t('stesso modello')
    case 'model_differs': return t('modello diverso')
    case 'medical': return t('riconosciuto medicale')
    case 'kind': return t('tipo generico')
    case 'seen': return t('visto, non riconosciuto')
    case 'not_seen': return t('non visto')
  }
}

function toneStato(s: ReferenceStatus): string {
  if (s === 'model' || s === 'medical') return 'badge badge--ok'
  if (s === 'model_differs' || s === 'kind' || s === 'seen') return 'badge badge--warn'
  return 'badge badge--error'
}

function etichettaVistoDa(k: string): string {
  switch (k) {
    case 'switch': return t('porta switch')
    case 'listen': return t('ascolto')
    case 'cmdb': return 'CMDB'
    case 'wire': return t('filo')
    default: return k
  }
}

const pct = (a?: number, b?: number) => (b ? `${Math.round(((a ?? 0) * 100) / b)}%` : '—')
/** The same share as a number, for sorting and filtering (null when there is no total). */
const quota = (a?: number, b?: number) => (b ? Math.round(((a ?? 0) * 100) / b) : null)

/** I gruppi di stati che si aprono dai numeri. */
type Gruppo = 'all' | 'visible' | 'recognised' | 'model' | 'not_seen'
const NEL_GRUPPO: Record<Gruppo, (s: ReferenceStatus) => boolean> = {
  all: () => true,
  visible: (s) => s !== 'not_seen',
  recognised: (s) => s === 'model' || s === 'medical' || s === 'model_differs',
  model: (s) => s === 'model',
  not_seen: (s) => s === 'not_seen',
}

export function RiferimentoDispositivi() {
  const { principal } = useAuth()
  const ruoli = principal?.roles ?? []
  const admin = ruoli.includes('tenant_admin') || ruoli.includes('provider_admin')
  const piattaforma = ruoli.includes('provider_admin')
  const dati = useAsync(() => api.referenceComparison(), [])
  const [fonte, setFonte] = useState<ReferenceSourceKey>('armis')
  const [lavoro, setLavoro] = useState<string | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const [lista, setLista] = useState<{ titolo: string; gruppo: Gruppo; tipo?: string } | null>(null)
  const [firmeAperte, setFirmeAperte] = useState(false)
  const input = useRef<HTMLInputElement>(null)

  async function importa(file: File) {
    setErrore(null)
    setEsito(null)
    setLavoro(t('Lettura del file…'))
    try {
      const fogli = await leggiFoglio(file)
      const foglio = fogliConDispositivi(fogli)
      if (!foglio) throw new Error(t('Nel file non c’è un foglio con una colonna di MAC o di IP.'))
      if (!window.confirm(t('Importare {n} righe dal foglio «{foglio}» come inventario {fonte}? Sostituisce l’import precedente della stessa fonte.', { n: foglio.righe.length, foglio: foglio.nome, fonte: etichettaFonte(fonte) }))) return
      setLavoro(t('Import in corso…'))
      const r = await api.importReferences(fonte, file.name, tabellaTsv(foglio))
      setEsito(t('Importati {n} dispositivi ({s} righe saltate: senza MAC né IP o ripetute). Colonne riconosciute: {c}.', { n: r.imported, s: r.skipped, c: r.columns.join(', ') }))
      dati.reload()
    } catch (e) {
      setErrore(e)
    } finally {
      setLavoro(null)
      if (input.current) input.current.value = ''
    }
  }

  async function elimina(k: ReferenceSourceKey) {
    if (!window.confirm(t('Eliminare l’inventario {fonte} importato? Il catalogo CENSO e ciò che ha già imparato restano.', { fonte: etichettaFonte(k) }))) return
    setErrore(null)
    try {
      await api.deleteReferences(k)
      dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }

  const d = dati.data
  const tot: ReferenceCounts = d?.totals ?? {}
  const n = (v?: number) => (v ?? 0).toLocaleString(localeCorrente())

  return (
    <Card title={t('Confronto con un inventario di riferimento')}>
      <p className="field__hint" style={{ marginTop: 0 }}>
        {t('Importa da Armis, Medigate o dall’Ingegneria Clinica un elenco Excel o CSV con MAC o IP. CENSO verifica per ogni dispositivo se è presente nella rete e se viene riconosciuto, confrontando i dati raccolti da switch, ascolto passivo, CMDB e traffico di rete. I dispositivi identificati contribuiscono inoltre a creare firme utili a riconoscere automaticamente gli stessi modelli.')}
      </p>
      <ErrorBanner error={dati.error} />
      <ErrorBanner error={errore} />
      {dati.loading && <Spinner />}
      {admin && (
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
          <select value={fonte} onChange={(e) => setFonte(e.target.value as ReferenceSourceKey)} aria-label={t('Fonte')}>
            {FONTI.map((f) => <option key={f.key} value={f.key}>{etichettaFonte(f.key)}</option>)}
          </select>
          <input ref={input} type="file" accept=".xlsx,.csv,.tsv,.txt" style={{ display: 'none' }}
                 onChange={(e) => { const f = e.target.files?.[0]; if (f) void importa(f) }} />
          <button type="button" onClick={() => input.current?.click()} disabled={lavoro != null}>
            {t('Importa file')}
          </button>
          {lavoro && <span className="field__hint">{lavoro}</span>}
        </div>
      )}
      {esito && <p className="field__hint">{esito}</p>}
      {d && d.sources.length === 0 && (
        <p className="text-muted" style={{ margin: 0 }}>{t('Nessun inventario importato.')}</p>
      )}
      {d && d.sources.length > 0 && (
        <>
          <p className="field__hint">
            {d.sources.map((s) => (
              <span key={s.source} style={{ marginRight: 12 }}>
                {etichettaFonte(s.source)}: {t('{n} dispositivi', { n: n(s.count) })}
                {s.imported_at && ` · ${new Date(s.imported_at).toLocaleString(localeCorrente())}`}
                {admin && (
                  <> · <button type="button" className="linklike" onClick={() => void elimina(s.source)}>{t('elimina')}</button></>
                )}
              </span>
            ))}
          </p>
          <div className="stats">
            <StatCard label={t('Nell’inventario')} value={n(tot.total)} onClick={() => setLista({ titolo: t('Nell’inventario'), gruppo: 'all' })} />
            <StatCard label={t('Visti da CENSO')} value={n(tot.visible)} hint={pct(tot.visible, tot.total)} onClick={() => setLista({ titolo: t('Visti da CENSO'), gruppo: 'visible' })} />
            <StatCard label={t('Riconosciuti')} value={n(tot.recognised)} hint={t('{p} · stesso modello o elettromedicale', { p: pct(tot.recognised, tot.total) })}
                      tone={(tot.recognised ?? 0) / (tot.total || 1) >= 0.9 ? 'ok' : undefined}
                      onClick={() => setLista({ titolo: t('Riconosciuti'), gruppo: 'recognised' })} />
            <StatCard label={t('Con lo stesso modello')} value={n(tot.model)} hint={pct(tot.model, tot.total)} onClick={() => setLista({ titolo: t('Con lo stesso modello'), gruppo: 'model' })} />
            <StatCard label={t('Non visti')} value={n(tot.not_seen)} hint={pct(tot.not_seen, tot.total)} tone={tot.not_seen ? 'warn' : undefined}
                      onClick={() => setLista({ titolo: t('Non visti'), gruppo: 'not_seen' })} />
          </div>
          <div style={{ marginTop: 12 }}>
            <DataTable
              rows={d.by_type}
              rowKey={(g) => g.key}
              conteggio={{ nome: t('tipi') }}
              onRowClick={(g) => setLista({ titolo: g.key, gruppo: 'all', tipo: g.key })}
              columns={[
                { key: 'key', header: t('Tipo') },
                { key: 'total', header: t('Dispositivi'), type: 'number', value: (g) => g.total ?? 0, render: (g) => n(g.total) },
                { key: 'visible', header: t('Visti'), type: 'number', value: (g) => quota(g.visible, g.total), render: (g) => pct(g.visible, g.total) },
                { key: 'recognised', header: t('Riconosciuti'), type: 'number', value: (g) => quota(g.recognised, g.total), render: (g) => pct(g.recognised, g.total) },
                { key: 'model', header: t('Stesso modello'), type: 'number', value: (g) => quota(g.model, g.total), render: (g) => pct(g.model, g.total) },
              ]}
            />
          </div>
          {admin && (
            <div className="rowactions" style={{ textAlign: 'left', marginTop: 8 }}>
              <button type="button" className="secondary" onClick={() => setFirmeAperte(true)}>{t('Firme da imparare')}</button>
            </div>
          )}
        </>
      )}
      {lista && d && (
        <ListaRiferimento titolo={lista.titolo} righe={d.rows.filter((r) => NEL_GRUPPO[lista.gruppo](r.status) && (!lista.tipo || (r.device_type || '—') === lista.tipo))}
                          onChiudi={() => setLista(null)} />
      )}
      {firmeAperte && <FirmeRiferimento piattaforma={piattaforma} onChiudi={() => setFirmeAperte(false)} />}
    </Card>
  )
}

/** La lista device per device, con ciò che CENSO vede di ognuno. */
function ListaRiferimento({ titolo, righe, onChiudi }: { titolo: string; righe: ReferenceRow[]; onChiudi: () => void }) {
  const dove = (r: ReferenceRow) =>
    [r.censo.ci_name, r.censo.port?.switch_name, r.censo.port?.if_name, r.censo.port?.vlan != null ? `VLAN ${r.censo.port.vlan}` : null]
      .filter(Boolean).join(' ')
  const censoDice = (r: ReferenceRow) =>
    [r.censo.model, r.censo.kind, r.censo.medical ? `${t('medicale')}: ${r.censo.medical}` : null, r.censo.vendor].filter(Boolean).join(' · ')
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={titolo}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{titolo}</h3>
            <p className="dob-modal__sotto">{t('{n} dispositivi', { n: righe.length })}</p>
          </div>
          <div className="dob-modal__comandi">
            <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        <div className="dob-modal__corpo">
          <DataTable
            rows={righe}
            rowKey={(r) => r.id}
            conteggio={{ nome: t('dispositivi') }}
            emptyText={t('Nessun dispositivo per questo filtro.')}
            columns={[
              {
                key: 'device', header: t('Dispositivo'),
                value: (r) => [r.brand, r.model, r.device_type, r.name, r.location].filter(Boolean).join(' '),
                render: (r) => (
                  <>
                    <div>{[r.brand, r.model].filter(Boolean).join(' ') || '—'}</div>
                    <small className="text-muted">{[r.device_type, r.name, r.location].filter(Boolean).join(' · ')}</small>
                  </>
                ),
              },
              {
                key: 'addresses',
                header: <span title={t('Come li scrive l’inventario di riferimento')}>{t('MAC / IP dell’inventario')}</span>,
                label: t('MAC / IP dell’inventario'),
                mono: true,
                value: (r) => [...r.macs, ...r.ips].join(' '),
                render: (r) => <small>{r.macs.join(', ')}{r.ips.length > 0 && <><br />{r.ips.join(', ')}</>}</small>,
              },
              {
                key: 'status', header: t('Esito'), value: (r) => etichettaStato(r.status),
                render: (r) => <span className={toneStato(r.status)}>{etichettaStato(r.status)}</span>,
              },
              {
                key: 'seen_by', header: t('Visto da'), value: (r) => r.seen_by.map(etichettaVistoDa).join(', '),
                render: (r) => <small>{r.seen_by.map(etichettaVistoDa).join(', ') || '—'}</small>,
              },
              {
                key: 'censo', header: t('CENSO dice'), value: censoDice,
                render: (r) => <small>{censoDice(r) || '—'}</small>,
              },
              {
                key: 'where', header: t('Dove'), value: dove,
                render: (r) => (
                  <small>
                    {r.censo.ci_id && <Link to={`/cis/${r.censo.ci_id}`}>{r.censo.ci_name}</Link>}
                    {r.censo.port && (
                      <>{r.censo.ci_id && <br />}<Link to={`/cis/${r.censo.port.switch_id}`}>{r.censo.port.switch_name}</Link> {r.censo.port.if_name}{r.censo.port.vlan != null && ` · VLAN ${r.censo.port.vlan}`}</>
                    )}
                    {!r.censo.ci_id && !r.censo.port && '—'}
                  </small>
                ),
              },
            ]}
          />
        </div>
      </div>
    </div>
  )
}

function motivoAmbiguo(reason: string, ieee?: string | null): string {
  if (reason === 'it') return t('PC o server di marca generica: servono altri segnali')
  if (reason === 'oem') return t('prefisso di chi fa la scheda o il PC ({v}), non del costruttore del dispositivo', { v: ieee ?? '?' })
  if (reason === 'os') return t('impronta DHCP del sistema operativo (Windows, Linux): uguale su ogni PC con quella scheda')
  return t('tipi o modelli diversi con la stessa firma')
}

function etichettaProposta(p: ReferenceProposal): string {
  if (p.kind === 'prefix_model') return t('prefisso → modello')
  if (p.kind === 'prefix_type') return t('prefisso → tipo')
  if (p.kind === 'hostname') return t('nome del prodotto → modello')
  if (p.kind === 'announce') return t('annuncio in rete → modello')
  return t('impronta DHCP → modello')
}

/**
 * The signatures the inventory teaches. Everything not yet known is
 * preselected: local names are learnt for this hospital only, the rest for
 * every tenant; OEM prefixes and OS fingerprints are never proposed. Only the platform administration writes
 * them, since the recognition database is shared.
 */
function FirmeRiferimento({ piattaforma, onChiudi }: { piattaforma: boolean; onChiudi: () => void }) {
  const dati = useAsync(() => api.referenceSignatures(), [])
  const [scelte, setScelte] = useState<Set<string> | null>(null)
  const [esito, setEsito] = useState<string | null>(null)
  const [errore, setErrore] = useState<unknown>(null)
  const proposte = dati.data?.proposals ?? []
  const selezione = scelte ?? new Set(proposte.filter((p) => !p.existing).map((p) => p.key))
  const firma = (p: ReferenceProposal) => (p.kind === 'hostname' ? p.token : p.kind === 'announce' ? p.port : p.oui) ?? ''
  const nota = (p: ReferenceProposal) => [
    p.oem ? `OEM ${t('prefisso di {v}', { v: p.ieee_vendor ?? '?' })}` : '',
    p.local_name ? `${t('nome locale')} ${t('nome dato dall’ospedale, non dal costruttore')}` : '',
    p.oem || p.local_name ? t('si impara solo per questo ospedale') : '',
    p.existing ? t('già nel catalogo: {v}', { v: [p.existing.vendor, p.existing.model, p.existing.category].filter(Boolean).join(' ') }) : '',
  ].filter(Boolean).join(' ')
  async function impara() {
    if (!window.confirm(t('Scrivere {n} firme nella base di riconoscimento CENSO? I nomi dati dall’ospedale valgono solo per questo ospedale, le altre per tutti i tenant. Verifica che il contratto con il fornitore dell’inventario consenta di usarne le classificazioni.', { n: selezione.size }))) return
    setErrore(null)
    try {
      const r = await api.learnReferences([...selezione])
      const w = r.written
      setEsito(t('Scritte nel catalogo: {p} prefissi, {f} impronte; {a} dispositivi riconosciuti subito dalle impronte.', { p: (w.prefix_model ?? 0) + (w.prefix_type ?? 0), f: w.fingerprint ?? 0, a: w.applied ?? 0 }))
      dati.reload()
    } catch (e) {
      setErrore(e)
    }
  }
  return (
    <div className="dob-modal" role="dialog" aria-modal="true" aria-label={t('Firme da imparare')}>
      <div className="dob-modal__scrim" onClick={onChiudi} />
      <div className="dob-modal__pannello">
        <div className="dob-modal__testata">
          <div>
            <h3 className="dob-modal__titolo">{t('Firme da imparare')}</h3>
            <p className="dob-modal__sotto">{t('{n} proposte, {s} selezionate', { n: proposte.length, s: selezione.size })}</p>
          </div>
          <div className="dob-modal__comandi">
            {piattaforma && <button type="button" onClick={() => void impara()} disabled={selezione.size === 0}>{t('Impara nel catalogo CENSO')}</button>}
            <button type="button" className="secondary" onClick={onChiudi}>{t('Chiudi')}</button>
          </div>
        </div>
        <ErrorBanner error={dati.error} />
        <ErrorBanner error={errore} />
        {esito && <p className="field__hint">{esito}</p>}
        {!piattaforma && <p className="field__hint">{t('Le firme le scrive nella base di riconoscimento l’amministrazione della piattaforma, perché la base è condivisa fra i tenant.')}</p>}
        {dati.loading && <Spinner />}
        {dati.data && (
          <div className="dob-modal__corpo">
            <p className="field__hint" style={{ marginTop: 0 }}>
              {t('Un prefisso MAC del costruttore del dispositivo che nell’inventario porta sempre lo stesso modello (o lo stesso tipo e marca), un’impronta DHCP o un nome che portano sempre lo stesso modello; i nomi dati dall’ospedale si imparano solo per questo ospedale. Non diventano mai una regola il prefisso di chi fa la scheda o il PC (OEM), l’impronta DHCP di Windows o Linux, la sola porta vista nei flussi e i PC o server di marca generica: dicono chi ha fatto la scheda o che sistema gira, non quale dispositivo è.')}
            </p>
            <DataTable
              rows={proposte}
              rowKey={(p) => p.key}
              conteggio={{ nome: t('firme') }}
              emptyText={t('Nessuna firma da proporre.')}
              // Only the platform administration writes the shared recognition database.
              selezione={piattaforma ? { chiavi: selezione, onCambia: (k) => setScelte(new Set(k)), id: (p) => p.key } : undefined}
              columns={[
                {
                  key: 'signature', header: t('Firma'),
                  value: (p) => [firma(p), etichettaProposta(p), p.dhcp_fingerprint, p.dhcp_vendor].filter(Boolean).join(' '),
                  render: (p) => (
                    <>
                      <span className="mono">{firma(p)}</span>
                      <br /><small className="text-muted">{etichettaProposta(p)}{p.dhcp_fingerprint && ` · ${p.dhcp_fingerprint}${p.dhcp_vendor ? ` · ${p.dhcp_vendor}` : ''}`}</small>
                    </>
                  ),
                },
                { key: 'devices', header: t('Dispositivi'), type: 'number' },
                {
                  key: 'teaches', header: t('Insegna'),
                  value: (p) => [p.vendor, p.model, p.category, p.medical ? t('medicale') : '', ...(p.models ?? [])].filter(Boolean).join(' '),
                  render: (p) => (
                    <>
                      {[p.vendor, p.model].filter(Boolean).join(' ')}
                      <br /><small className="text-muted">{p.category}{p.medical && ` · ${t('medicale')}`}{p.models && p.models.length > 1 && ` · ${p.models.join(', ')}`}</small>
                    </>
                  ),
                },
                { key: 'ci_class', header: t('Classe CI'), value: (p) => p.ci_class ?? '', render: (p) => <small>{p.ci_class ?? '—'}</small> },
                {
                  key: 'notes', header: t('Note'), value: nota,
                  render: (p) => (
                    <small>
                      {p.oem && <span className="badge badge--warn">OEM</span>}{p.oem && ` ${t('prefisso di {v}', { v: p.ieee_vendor ?? '?' })}`}
                      {p.local_name && <><span className="badge badge--warn">{t('nome locale')}</span> {t('nome dato dall’ospedale, non dal costruttore')}</>}
                      {(p.oem || p.local_name) && <><br />{t('si impara solo per questo ospedale')}</>}
                      {p.existing && <>{p.oem && <br />}{t('già nel catalogo: {v}', { v: [p.existing.vendor, p.existing.model, p.existing.category].filter(Boolean).join(' ') })}</>}
                    </small>
                  ),
                },
              ]}
            />
            {dati.data.ambiguous.length > 0 && (
              <details style={{ marginTop: 12 }}>
                <summary>{t('Dove il prefisso non basta ({n})', { n: dati.data.ambiguous.length })}</summary>
                <DataTable
                  rows={dati.data.ambiguous}
                  rowKey={(a) => `${a.oui}|${a.dhcp_fingerprint ?? ''}`}
                  conteggio={{ nome: t('firme') }}
                  columns={[
                    {
                      key: 'signature', header: t('Firma'), value: (a) => [a.oui, a.dhcp_fingerprint].filter(Boolean).join(' '),
                      render: (a) => <><span className="mono">{a.oui}</span>{a.dhcp_fingerprint && <><br /><small className="text-muted">{a.dhcp_fingerprint}</small></>}</>,
                    },
                    { key: 'devices', header: t('Dispositivi'), type: 'number' },
                    {
                      key: 'reason', header: t('Perché'), value: (a) => motivoAmbiguo(a.reason, a.ieee_vendor),
                      render: (a) => <small>{motivoAmbiguo(a.reason, a.ieee_vendor)}</small>,
                    },
                    {
                      key: 'kinds', header: t('Tipi e modelli'), value: (a) => [...a.types, ...a.models].join(' · '),
                      render: (a) => <small>{[...a.types, ...a.models].join(' · ')}</small>,
                    },
                  ]}
                />
              </details>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
